// context/ApiContext/apiContext.js
import React, { useState, createContext, useContext, useEffect, useReducer, useRef } from 'react';
import { apiReducer, initialState, ActionTypes } from './apiReducer';

const ApiStateContext = createContext(undefined);
const ApiDispatchContext = createContext(undefined);

export const ApiProvider = ({ children }) => {
  const [state, dispatch] = useReducer(apiReducer, initialState);
  const updateQueueRef = useRef([]);
  const isProcessingRef = useRef(false);
  const processingTimeoutRef = useRef(null);

  // Process the execution status update queue
  const processQueue = async () => {
    // If already processing or queue is empty, return
    if (isProcessingRef.current || updateQueueRef.current.length === 0) {
      return;
    }

    // Set processing flag
    isProcessingRef.current = true;

    try {
      // Get the next update from the queue
      const nextUpdate = updateQueueRef.current.shift();
      
      // Process the update
      if (typeof nextUpdate === 'function') {
        // If it's a function, call it with the current state
        const updatedStatus = nextUpdate(state.executionStatus);
        dispatch({
          type: ActionTypes.SET_EXECUTION_STATUS,
          payload: { status: updatedStatus }
        });
      } else {
        // If it's a direct value, use it as is
        dispatch({
          type: ActionTypes.SET_EXECUTION_STATUS,
          payload: { status: nextUpdate }
        });
      }
      
      // Schedule the next queue processing with a small delay
      // to allow React to process the current update
      processingTimeoutRef.current = setTimeout(() => {
        // Clear processing flag
        isProcessingRef.current = false;
        
        // Process next item if queue is not empty
        if (updateQueueRef.current.length > 0) {
          processQueue();
        }
      }, 50); // Small delay to ensure React has time to process state
    } catch (error) {
      console.error("Error processing execution status update:", error);
      // Clear processing flag on error to prevent queue from getting stuck
      isProcessingRef.current = false;
      
      // Continue processing the queue despite error
      if (updateQueueRef.current.length > 0) {
        processQueue();
      }
    }
  };

  // Clean up timeouts when component unmounts
  useEffect(() => {
    return () => {
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
    };
  }, []);

  // Enhanced dispatch function that includes the queue processor
  const enhancedDispatch = (action) => {

    // Special case for clearing the queue
    if (action.type === ActionTypes.CLEAR_QUEUE) {
      console.log("ActionTypes.CLEAR_QUEUE");
      // Clear the queue
      updateQueueRef.current = [];
      
      // Clear any pending timeout
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
        processingTimeoutRef.current = null;
      }
      
      // Reset processing flag
      isProcessingRef.current = false;
      
      return; // Don't pass this action to the reducer
    
    // If this is an execution status update, handle it specially
    } else if (action.type === ActionTypes.SET_EXECUTION_STATUS) {
      // Get the status or function from the payload
      const statusOrFunction = action.payload.status;
      
      // Add to the queue
      updateQueueRef.current.push(statusOrFunction);
      
      // Start processing if not already processing
      if (!isProcessingRef.current) {
        processQueue();
      }
    } else {
      // For all other actions, dispatch directly
      dispatch(action);
    }
  };

  return (
    <ApiStateContext.Provider value={state}>
      <ApiDispatchContext.Provider value={enhancedDispatch}>
        {children}
      </ApiDispatchContext.Provider>
    </ApiStateContext.Provider>
  );
};

// Custom hooks for consuming context
export const useApiState = () => {
  const context = useContext(ApiStateContext);
  if (context === undefined) {
    throw new Error('useApiState must be used within an ApiProvider');
  }
  return context;
};

export const useApiDispatch = () => {
  const context = useContext(ApiDispatchContext);
  if (context === undefined) {
    throw new Error('useApiDispatch must be used within an ApiProvider');
  }
  return context;
};

// Convenience hook that provides actions
export const useApi = () => {
  const state = useApiState();
  const dispatch = useApiDispatch();

  // Set Credentials
  const setCredentials = (credentials) => {
    dispatch({
      type: ActionTypes.SET_CREDENTIALS,
      payload: credentials
    });
  };
  
  // Set API Interval
  const setApiInterval = (interval) => {
    dispatch({
      type: ActionTypes.SET_API_INTERVAL,
      payload: interval
    });
  };

  const setUplynkCentral = (data) => {
    dispatch({
      type: ActionTypes.SET_UPLYNK_CENTRAL,
      payload: data
    });
  };

  // Update partial uplynkCentral data (merges with existing)
  const updateUplynkCentral = (data) => {
    dispatch({
      type: ActionTypes.UPDATE_UPLYNK_CENTRAL,
      payload: data
    });
  };

  const appendUplynkJobState = (jobState) => {
    dispatch({
      type: ActionTypes.APPEND_UPLYNK_JOB_STATE,
      payload: jobState
    });
  };  

  // Clear all uplynkCentral data
  const clearUplynkCentral = () => {
    dispatch({
      type: ActionTypes.CLEAR_UPLYNK_CENTRAL
    });
  };


  // Register a module configuration for a node
  // const registerModule = (nodeId, moduleConfig, label) => {
  //   dispatch({ 
  //     type: ActionTypes.REGISTER_MODULE, 
  //     payload: { 
  //       nodeId,
  //       label,
  //       moduleConfig: {
  //         ...moduleConfig,
  //         readyState: false,
  //         activeState: false
  //       } 
  //     } 
  //   });
  // };
  const registerModule = (nodeId, moduleConfig, label, validation = null) => {
    // Calculate readyState based on validation if provided
    const readyState = validation ? validation.isValid : false;
    
    dispatch({ 
      type: ActionTypes.REGISTER_MODULE, 
      payload: { 
        nodeId,
        label,
        moduleConfig: {
          ...moduleConfig,
          readyState,
          activeState: readyState // Set activeState to match readyState
        } 
      } 
    });
  };
  

  // Update an existing module configuration
  const updateModuleConfig = (nodeId, endpoints, validation = null) => {
    dispatch({ 
      type: ActionTypes.UPDATE_MODULE_CONFIG, 
      payload: { 
        nodeId, 
        endpoints,
        validation 
      } 
    });
  };  

  // Update API parameters for a specific node and endpoint index
  const updateApiParams = (nodeId, endpointIndex, params) => {
    dispatch({ 
      type: ActionTypes.UPDATE_API_PARAMS, 
      payload: { nodeId, endpointIndex, params } 
    });
  };

  // Set the execution order of nodes
  const setExecutionOrder = (nodeIds) => {
    dispatch({ 
      type: ActionTypes.SET_EXECUTION_ORDER, 
      payload: nodeIds 
    });
  };

  // Reorder a specific node in the execution sequence
  const reorderExecution = (nodeId, newPosition) => {
    dispatch({ 
      type: ActionTypes.REORDER_EXECUTION, 
      payload: { nodeId, newPosition } 
    });
  };

  // Remove a module configuration
  const removeModule = (nodeId) => {
    if (!nodeId || !state.moduleNodes[nodeId]) {
      console.warn('Skipping Removal of Module/NodeID from API Stat:', nodeId);
      return; // Optional early return
    }
    dispatch({ type: ActionTypes.REMOVE_MODULE, payload: nodeId });
  };

  const toggleModuleActive = (nodeId) => {
    dispatch({
      type: ActionTypes.TOGGLE_MODULE_ACTIVE,
      payload: nodeId
    });
  };

  // Set execution status for tracking API calls
  // This now supports both direct status objects and functional updates
  const setExecutionStatus = (statusOrFunction) => {
    // console.log("__CONTEXT - setExecutionStatus: ", statusOrFunction);
    dispatch({
      type: ActionTypes.SET_EXECUTION_STATUS,
      payload: { status: statusOrFunction }
    });
  };

  // Add this function to the useApi hook
  const setProcessingState = (key, value) => {
    // console.log(`Setting processing state: ${key} = ${value}`);
    dispatch({
      type: ActionTypes.SET_PROCESSING_STATE,
      payload: { key, value }
    });
  };

  // CLOUD PROCESSING STATE
  const setIsCloudProcessRunning = (isRunning) => {
    setProcessingState('isCloudProcessRunning', isRunning);
  };
  
  const updateSharedParams = (paramsUpdate) => {
    dispatch({
      type: ActionTypes.UPDATE_SHARED_PARAMS,
      payload: paramsUpdate
    });
  };

  const resetState = () => {
    // First clear the queue (this will be handled by enhancedDispatch)
    dispatch({
      type: ActionTypes.CLEAR_QUEUE
    });

    // Dispatch the reset action
    dispatch({
      type: ActionTypes.RESET_STATE
    });
  };

  return {
    state,
    sharedParams: state.sharedParams,
    credentials: state.credentials,
    uplynkCentral: state.uplynkCentral,
    setUplynkCentral,
    updateUplynkCentral,
    appendUplynkJobState,
    clearUplynkCentral,
    apiInterval: state.apiInterval,
    setCredentials,
    setApiInterval,
    moduleNodes: state.moduleNodes,
    executionOrder: state.executionOrder,
    executionStatus: state.executionStatus,
    registerModule,
    updateModuleConfig,
    updateApiParams,
    setExecutionOrder,
    reorderExecution,
    removeModule,
    toggleModuleActive,
    setExecutionStatus,
    isExecuting: state.executionStatus.isExecuting, // Add this line
    isCloudProcessRunning: state.processingStates.isCloudProcessRunning,
    setIsCloudProcessRunning,
    setProcessingState,
    updateSharedParams,
    resetState
  };
};